/**
 * Persona workspace resolution — the pure decision layer behind
 * `/workspace/:persona`.
 *
 * The shell's landing surfaces have historically been MODULE-first: `/browse`
 * lists the catalogue and the operator has to know which module owns the work
 * they came to do. A persona workspace inverts that: it opens on the work
 * surfaces the persona's own role is built around, and never lists a module the
 * persona doesn't work in.
 *
 * WHAT THIS FILE OWNS
 * -------------------
 * Only the decision. Given the URL's persona segment, the shell's fail-closed
 * {@link ModuleAccess} gate, and the registry-derived list of work surfaces,
 * it returns exactly ONE state for the route to render. It touches no React,
 * no registries and no DOM, so every branch below is unit-testable.
 *
 * THE ORDER OF THE BRANCHES IS THE SECURITY CONTRACT
 * --------------------------------------------------
 *   1. An unrecognised persona is `not-found`. The persona catalogue is static,
 *      client-side manifest data (`PERSONA_WORKSPACES`), so answering this
 *      before entitlement leaks nothing about the principal — and it means a
 *      typo'd URL renders a real not-found state instead of an empty page.
 *   2. `unavailable` — the entitlement evaluation failed. Nothing is listed.
 *   3. `loading`     — no authoritative answer yet. Nothing is listed. There is
 *                      deliberately no "show it now, filter it later" first
 *                      paint.
 *   4. `denied`      — entitlement resolved and NONE of the persona's modules
 *                      are permitted to this principal. An explicit state, not
 *                      an empty grid.
 *   5. `ready`       — the persona's modules ∩ the permitted set.
 *
 * The persona manifest is a PRESENTATION filter and can only ever narrow: every
 * surface returned here has already been intersected with
 * `access.permittedModuleIds`, which is itself the evaluator's answer narrowed
 * by the manifest catalogue. Nothing in this file can widen what a principal
 * may see, and no branch turns a missing answer into a grant.
 */
import type { PersonaWorkspaceManifest } from '@tyndo/types';
import type { ModuleAccess } from './entitlementGate';

/**
 * Catalogue module ids are provider-namespaced (`tyndo.procurement`); the
 * persona manifests in `@tyndo/types` name the bare module (`procurement`),
 * because they are authored against `docs/module-catalog.json` rather than
 * against a running manifest. Comparing the two forms directly matches nothing,
 * so every comparison in this file goes through the bare form.
 */
export function bareModuleId(id: string): string {
  const idx = id.lastIndexOf('.');
  return idx >= 0 ? id.slice(idx + 1) : id;
}

/** One deep-linkable work surface inside a module the persona works in. */
export interface WorkSurface {
  /** Catalogue module id that owns the surface, e.g. `tyndo.procurement`. */
  moduleId: string;
  /** Human label as the owning module registered it, e.g. "Requisitions". */
  label: string;
  /** In-app path the surface opens at, e.g. `/tyndo.procurement/requisitions`. */
  path: string;
}

/** The persona's surfaces for one module, in the module's own menu order. */
export interface WorkSurfaceGroup {
  moduleId: string;
  /** Module display name from the manifest registry (falls back to the id). */
  moduleName: string;
  surfaces: WorkSurface[];
}

export type PersonaWorkspaceState =
  /** The URL segment matches no persona in the manifest. */
  | { kind: 'not-found'; persona: string }
  /** Entitlements are still resolving — nothing is listed. */
  | { kind: 'loading'; workspace: PersonaWorkspaceManifest }
  /** The entitlement service did not answer — nothing is listed. */
  | { kind: 'unavailable'; workspace: PersonaWorkspaceManifest }
  /** Resolved, and this principal may open none of the persona's modules. */
  | { kind: 'denied'; workspace: PersonaWorkspaceManifest }
  /** Resolved, with at least one permitted module. */
  | {
      kind: 'ready';
      workspace: PersonaWorkspaceManifest;
      /** Groups for `primaryModules`, in the manifest's declared order. */
      primary: WorkSurfaceGroup[];
      /** Groups for `secondaryModules`, in the manifest's declared order. */
      secondary: WorkSurfaceGroup[];
    };

export interface PersonaWorkspaceInput {
  /** The `:persona` route segment. */
  persona: string | null | undefined;
  /**
   * The persona catalogue to resolve against — `PERSONA_WORKSPACES` at the one
   * call site. Passed in rather than imported so this module carries no runtime
   * dependency on `@tyndo/types` (whose barrel is bundler-resolved and cannot
   * be loaded by the shell's `node --test` runner), and so a test can pin the
   * branches against a fixture catalogue.
   */
  catalogue: readonly PersonaWorkspaceManifest[];
  /** The shell's single fail-closed entitlement gate. */
  access: ModuleAccess;
  /**
   * Every module-scoped work surface the registries currently know about — the
   * widest possible list. It is filtered here; the caller must not pre-filter
   * it by persona, or the "denied" state could not be distinguished from
   * "this persona has no surfaces registered".
   */
  surfaces: readonly WorkSurface[];
  /** Display names by catalogue module id, from the manifest registry. */
  moduleNames?: ReadonlyMap<string, string>;
}

/** Look a persona up by its `PersonaTag` id. Total: unknown ids yield null. */
export function findPersonaWorkspace(
  persona: string | null | undefined,
  catalogue: readonly PersonaWorkspaceManifest[],
): PersonaWorkspaceManifest | null {
  if (!persona) return null;
  return catalogue.find((p) => p.id === persona) ?? null;
}

/**
 * Group the permitted surfaces for one list of persona modules.
 *
 * `personaModules` is in the manifest's declared order and drives the output
 * order, so the persona's own priority (not registry insertion order) decides
 * what the operator reads first. A module the persona names but the principal
 * may not open produces NO group at all — an unentitled module must not be
 * visible as an empty heading either.
 */
function groupsFor(
  personaModules: readonly string[],
  access: ModuleAccess,
  surfaces: readonly WorkSurface[],
  moduleNames: ReadonlyMap<string, string> | undefined,
): WorkSurfaceGroup[] {
  const permittedByBare = new Map<string, string>();
  for (const id of access.permittedModuleIds) {
    permittedByBare.set(bareModuleId(id), id);
  }

  const groups: WorkSurfaceGroup[] = [];
  for (const wanted of personaModules) {
    const moduleId = permittedByBare.get(bareModuleId(wanted));
    if (!moduleId) continue;
    groups.push({
      moduleId,
      moduleName: moduleNames?.get(moduleId) ?? moduleId,
      surfaces: surfaces.filter((s) => s.moduleId === moduleId),
    });
  }
  return groups;
}

/** Resolve the one state `/workspace/:persona` should render. */
export function resolvePersonaWorkspaceState(
  input: PersonaWorkspaceInput,
): PersonaWorkspaceState {
  const workspace = findPersonaWorkspace(input.persona, input.catalogue);
  if (!workspace) {
    return { kind: 'not-found', persona: input.persona ?? '' };
  }

  const { access } = input;
  if (access.state === 'unavailable') return { kind: 'unavailable', workspace };
  if (access.state === 'loading') return { kind: 'loading', workspace };

  const primary = groupsFor(
    workspace.primaryModules,
    access,
    input.surfaces,
    input.moduleNames,
  );
  const secondary = groupsFor(
    workspace.secondaryModules,
    access,
    input.surfaces,
    input.moduleNames,
  );

  if (primary.length === 0 && secondary.length === 0) {
    return { kind: 'denied', workspace };
  }
  return { kind: 'ready', workspace, primary, secondary };
}
